import React, { useEffect, useState } from 'react'
import { ProjectProps } from '../Project'
import { AuthModel } from '../../../utils/interfaces'
import { useTranslation } from 'react-i18next'
import AlertWrapper from '../../../components/AlertWrapper'
import Alert from '../../../components/Alert'
import { Input } from '../../../components/Input'
import { Admin } from '../../../api/API'
import { useAsyncEffect, useEventListener } from '../../../utils/functions'
import Loading from '../../../components/Loading'
import Table from '../../../components/Table'
import { BoldHeader3 } from '../../../components/General'
import { freeLimits, proLimits } from '../../../utils/constants'

interface Props extends ProjectProps {
  auth: AuthModel
}

const Users = ({ auth, ...props }: Props) => {

  const { t } = useTranslation();
  const [users, setUsers] = useState<any[]>([]);
  const [userInfos, setUserInfos] = useState<Record<string, any>[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [showDeleteUsersAlert, setShowDeleteUsersAlert] = useState(false);
  const [showAddUserAlert, setShowAddUserAlert] = useState(false);
  const [addUserError, setAddUserError] = useState<string>('');
  const [newUsername, setNewUsername] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // filtered users info
  useEffect(() => {
    let filtered = users.map(u => { return { [t('table-attr.auth.username')]: u.Username, Email: u.Email, [t('table-attr.auth.email-verified')]: u.EmailVerified } });
    setUserInfos(filtered);
  }, [users]);

  useEffect(() => {
    setNewUserEmail('');
    setNewUserPassword('');
    setNewUsername('');
  }, [showAddUserAlert]);

  useAsyncEffect(async () => {
    const udata = await Admin.retrieveUsers(auth.auth.userPoolId);
    setUsers(Array.isArray(udata) ? udata : []);
    setIsLoading(false);
  }, [auth]);

  // event handlers
  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.add-user-button') && !target.closest('.add-user-alert')) {
      setShowAddUserAlert(false);
    }
    if (!target.closest('.delete-users-button') && !target.closest('.delete-users-alert')) {
      setShowDeleteUsersAlert(false);
    }
    if (!target.closest('.add-user-error-alert')) {
      setAddUserError('');
    }
  })

  // check if user amount reached limit
  const hasReachedLimit = () => {
    return props.user?.plan.name === 'pro' ? 
      users.length >= proLimits.users : props.user?.plan.name === 'free' ?
      users.length >= freeLimits.users : false;
  }

  // add new user
  const addUser = async () => {
    if (!auth) return;
    setShowAddUserAlert(false);
    const response = await Admin.addUser(auth.auth.userPoolId, { username: newUsername, email: newUserEmail, password: newUserPassword });
    if (response.error) {
      const err = JSON.parse(response.error as string).error;
      if (err.includes('UsernameExistsException')) {
        setAddUserError(t('project.auth.users.add-user.user-exists'));
      } else if (err.includes('InvalidPasswordException')) {
        setAddUserError(t('project.auth.users.add-user.invalid-params'));
      }
    }
    const udata = await Admin.retrieveUsers(auth.auth.userPoolId);
    setUsers(udata);
  }

  // delete users
  const deleteUsers = async () => {
    setShowDeleteUsersAlert(false);
    setUsers(prev => prev.filter(user => !selected.includes(user['Username'])));
    for (const username of selected) {
      await Admin.deleteUser(auth.auth.userPoolId, username);
    }
    setSelected([]);
  }

  return (
    isLoading ? <main className='flex items-center justify-center flex-1'><Loading solid size={8} /></main>
    : <>
      <div className='flex-1 p-2 flex flex-col overflow-auto'>
        <div className='flex items-center justify-between flex-wrap'>
          <BoldHeader3>{t('project.auth.users.title', { projectName: props.project.name })} ({users.length})</BoldHeader3>
          <div className='gap-4 flex my-1'>
            {users.length > 0 && <button onClick={() => setShowDeleteUsersAlert(true)} disabled={selected.length === 0} className='delete-users-button bg-gray-100 disabled:opacity-30 rounded-lg p-2 px-4 text-xs'>{t('project.auth.users.delete-users')}</button>}
            <button onClick={() => setShowAddUserAlert(true)} className='add-user-button bg-primary text-white rounded-lg p-2 px-4 text-xs'>{t('project.auth.users.add-user')}</button>
          </div>
        </div>
        <div className='flex flex-col flex-1 overflow-auto mt-2'>
          {users.length > 0 ? <Table
            data={userInfos}
            setData={setUserInfos}
            selected={selected}
            setSelected={setSelected}
            attributes={[ { name: t('table-attr.auth.username'), type: 'string' }, { name: 'Email', type: 'string' }, { name: t('table-attr.auth.email-verified'), type: 'boolean' } ]}
            attrKey={t('table-attr.auth.username')}
            displayBooleanAsIcon
          /> : <div className='flex-1 flex items-center justify-center font-light text-sm'>{t('project.auth.users.empty')}</div>}
        </div>
      </div>

      {/* add user */}
      {showAddUserAlert && <AlertWrapper>
        <Alert
          extraclass='add-user-alert'
          title={t('project.auth.users.add-user')}
          maxWidth={400}
          onClose={() => setShowAddUserAlert(false)}
          body={<div className='flex flex-col gap-2'>
            <div>
              <label className='text-xs text-gray-400 m-1'>{t('auth.username')}</label>
              <Input autoFocus value={newUsername} onChange={(e) => setNewUsername(e.target.value)} />
            </div>
            <div>
              <label className='text-xs text-gray-400 m-1'>Email</label>
              <Input value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} />
            </div>
            <div>
              <label className='text-xs text-gray-400 m-1'>{t('auth.password')}</label>
              <Input type='password' value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} />
            </div>
          </div>}
          bottom={<div>
            {hasReachedLimit() && <div className='text-xs m-1 text-red-400'>{t('project.auth.users.add-user.limit')}</div>}
            <div className='flex'><button onClick={addUser} disabled={hasReachedLimit() || !newUsername || !newUserEmail || !newUserPassword} className='flex items-center justify-center bg-primary disabled:bg-gray-300 text-white px-8 py-2 rounded-lg text-sm'>{t('general.add')}</button></div>
          </div>}
        />
      </AlertWrapper>}

      {/* add user error */}
      {addUserError && <AlertWrapper>
        <Alert
          extraclass='add-user-error-alert'
          title={t('project.auth.users.add-user.error')}
          maxWidth={400}
          onClose={() => setAddUserError('')}
          body={<div>
            <div>{addUserError}</div>
          </div>}
          bottom={<div className='flex'>
            <button onClick={() => setAddUserError('')} className='bg-primary text-white text-sm p-2 px-8 rounded-lg'>{t('general.close')}</button>
          </div>}
        />
      </AlertWrapper>}

      {/* delete users */}
      {showDeleteUsersAlert && <AlertWrapper>
        <Alert
          extraclass='delete-users-alert'
          title={t('project.auth.users.delete-users')}
          maxWidth={480}
          onClose={() => setShowDeleteUsersAlert(false)}
          body={<div className='mx-1'>
            <div>{t(selected.length === 1 ? 'project.auth.users.delete-user-message' : 'project.auth.users.delete-users-message', { count: selected.length })}</div>
          </div>}
          bottom={<div className='flex'>
            <button onClick={deleteUsers} disabled={selected.length === 0} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{t('general.delete')}</button>
          </div>}
        />
      </AlertWrapper>}
    </>
  )
}

export default Users